<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { navigate } from 'svelte-navigator';

  import questions from './questions.json';
  import { onMount } from 'svelte';
  import { handleFetchRetry } from '../handleFetchRetry';
  import { debugLog } from '../../lib/debug-log';

  interface response {
    [key: string]: string | null;
  }

  let responses: response = {};
  let purchasePaybackId: string;
  let allQuestionsAnswered = false;

  // read from localstorage
  if (localStorage.getItem('purchasePayBack') !== null) {
    purchasePaybackId = JSON.parse(
      localStorage.getItem('purchasePayBack') as string,
    ).id;
  }

  /* -----------------------------------------
      NEW FIELDS (Step 4 custom requirements)
  ----------------------------------------- */
  const getYesterdayDateString = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const year = yesterday.getFullYear();
    const month = String(yesterday.getMonth() + 1).padStart(2, '0');
    const day = String(yesterday.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const maxDate = getYesterdayDateString();

  let dateOfEvent = '';
  let isDateOfEventActive = false;
  let numberOfWheels = '';
  let licensePlate = '';
  let remarks = '';

  const damageOptions = [
    'Einfahrschaden',
    'Reifenplatzer',
    'Anfahrschaden',
    'Totalschaden Felge',
    'Diebstahl',
    'Vandalismus'
  ];

  let damageReasons: string[] = [];

  /* Reactive flag */
  $: aktenzeichenEnabled =
    damageReasons.includes('Diebstahl') ||
    damageReasons.includes('Vandalismus');

  /* NEW: clear value when not required */
  $: if (!aktenzeichenEnabled) {
      licensePlate = '';
    }

  /* Reactive validation */
  $: allQuestionsAnswered =
    Boolean(
      dateOfEvent &&
      numberOfWheels &&
      damageReasons.length > 0 &&
      (!aktenzeichenEnabled || licensePlate.trim().length > 0)
    );

  const toggleReason = (reason: string) => {
    if (damageReasons.includes(reason)) {
      damageReasons = damageReasons.filter((r) => r !== reason);
    } else {
      damageReasons = [...damageReasons, reason];
    }
  };

  const removeReason = (reason: string) => {
    damageReasons = damageReasons.filter((r) => r !== reason);
  };

  /* -----------------------------------------
      SAVE (IDENTICAL TO ORIGINAL)
  ----------------------------------------- */
  const saveQuestions = async () => {
    responses = {
      dateOfEvent,
      numberOfWheels,
      damageReasons: JSON.stringify(damageReasons),
      licensePlate: aktenzeichenEnabled ? licensePlate : null,
      remarks: remarks || null,
    };

    const item = localStorage.getItem('customerProcess');
    const customerProcess = item !== null ? JSON.parse(item as string) : null;

    localStorage.setItem('questionsResponses', JSON.stringify(responses));

    localStorage.setItem(
      'customerProcess',
      JSON.stringify({
        ...customerProcess,
        questions: true,
      }),
    );

    debugLog('Questions responses saved locally:', responses);

    let URL = `${process.env.SVELTE_APP_FE_REMOTE_URL}/api/mbdwic`;

    await handleFetchRetry(() =>
      fetch(`${URL}/purchasepaybacks/${purchasePaybackId}`, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json, text/plain',
          'Content-Type': 'application/json;charset=UTF-8',
          'X-Custom-Header': 'mb-cashback-2025-header',
        },
        body: JSON.stringify({
          questions: JSON.stringify(responses),
        }),
      }),
    );

    navigate('/step/5');
  };

  /* -----------------------------------------
      ORIGINAL STRUCTURE PRESERVED
  ----------------------------------------- */
  onMount(() => {
    responses = {
      dateOfEvent: null,
      numberOfWheels: null,
      damageReasons: null,
      licensePlate: null,
      remarks: null,
    };
  });
</script>

<!-- PAGE -->
<div class="space-y-8">
  <div class="space-y-4 overflow-auto">

    <!-- TITLE -->
    <h1 class="font-sans text-3xl font-bold tracking-wide text-primary uppercase">
      {$_('Schadensereignis')}
    </h1>

    <!-- INTRO TEXTS -->
    <p class="text-justify font-bold leading-relaxed text-cubiq-grey-font">
      {$_('Subtitle Text')}
    </p>

    <p class="text-justify leading-relaxed text-cubiq-grey-font whitespace-pre-line">
      {$_('Intro Text')}
    </p>

    <!-- FORM AREA -->
    <div class="space-y-6">

      <!-- DATE + NUMBER OF WHEELS -->
      <div class="grid md:grid-cols-2 gap-6">

        <div>
          <label for="dateOfEvent" class="block font-medium mb-1">
            {$_('Date of Event')} <span class="text-red-500">*</span>
          </label>
          <input
            id="dateOfEvent"
            type="date"
            class="input"
            max={maxDate}
            bind:value={dateOfEvent}
            on:click={(e) => {
              if (e.currentTarget.showPicker) {
                if (isDateOfEventActive) {
                  e.currentTarget.blur();
                  isDateOfEventActive = false;
                } else {
                  e.currentTarget.focus();
                  isDateOfEventActive = true;
                  try { 
                    e.currentTarget.showPicker(); 
                  } catch(err) {}
                }
              }
            }}
          />
        </div>

        <div>
          <label for="numberOfWheels" class="block font-medium mb-1">
            {$_('Number of Wheels')} <span class="text-red-500">*</span>
          </label>
          <select
            id="numberOfWheels"
            class="input"
            bind:value={numberOfWheels}
          >
            <option value="">{$_('Please Choose')}</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
          </select>
        </div>

      </div>

      <!-- DAMAGE REASONS -->
      <div class="space-y-2">
        <label for="damage-reasons" class="block font-medium mb-1">
          {$_('Reason for Damage')} <span class="text-red-500">*</span>
        </label>

        <div
          id="damage-reasons"
          class="border rounded-md p-3 min-h-[50px] flex flex-wrap gap-2 bg-white"
        >
          {#if damageReasons.length === 0}
            <span class="text-gray-400">{$_('Select Damage Reason')}</span>
          {/if}

          {#each damageReasons as item}
            <span class="px-2 py-1 bg-blue-100 text-blue-700 rounded flex items-center gap-1">
              {item}
              <button
                class="text-blue-700 hover:text-red-600"
                on:click={() => removeReason(item)}
              >
                ✕
              </button>
            </span>
          {/each}
        </div>

        <div class="grid grid-cols-3 gap-4 p-2 border rounded-md bg-gray-50">
          {#each damageOptions as option}
            <div
              role="button"
              tabindex="0"
              class={`border rounded-md p-3 text-center cursor-pointer transition
                ${
                  damageReasons.includes(option)
                    ? 'bg-blue-100 border-blue-500 text-blue-700'
                    : 'bg-white hover:bg-gray-100'
                }`}
              on:click={() => toggleReason(option)}
              on:keydown={(e) => e.key === 'Enter' && toggleReason(option)}
            >
              {option}
            </div>
          {/each}
        </div>
      </div>

      <!-- AKTENZEICHEN -->
      {#if aktenzeichenEnabled}
        <div class="space-y-1">
          <label for="licensePlate" class="block font-medium mb-1">
            {$_('Aktenzeichen')} <span class="text-red-500">*</span>
          </label>
          <input
            id="licensePlate"
            class="input"
            bind:value={licensePlate}
            placeholder={$_('Aktenzeichen Placeholder')}
          />
        </div>
      {/if}

      <!-- REMARKS -->
      <div>
        <label for="remarks" class="block font-medium mb-1">
          {$_('Remarks')}
        </label>
        <textarea
          id="remarks"
          class="input"
          rows="4"
          bind:value={remarks}
          placeholder={$_('Remarks Placeholder')}
        ></textarea>
      </div>

    </div>
  </div>

  <!-- FOOTER -->
  <div class="flex justify-end py-6">
    <div class="flex w-full justify-between">
      <p class="text-red-500">* {$_('required')}</p>

      <button
        class={allQuestionsAnswered ? 'button' : 'button-disabled'}
        type="submit"
        disabled={!allQuestionsAnswered}
        data-umami-event="next button on questions page clicked"
        data-umami-event-page="/step/4"
        on:click|preventDefault={saveQuestions}
      >
        {$_('Next')}
      </button>
    </div>
  </div>
</div>

<style>
  input[type="date"]::-webkit-calendar-picker-indicator {
    pointer-events: none;
  }
</style>